/**
 * W5 — Resolved workbench commands for GlobalSearch surfaces
 */

import { useEffect, useMemo } from 'react';
import type { UniversalBaseEntity } from '@shared/types/entity';
import type { WorkbenchCommandContext } from '@shared/business-workbench/command-resolver';
import type { WorkbenchFocus } from '@shared/business-workbench/workbench-focus';
import {
  deriveWorkbenchFocusFromWorkspaceState,
  type WorkbenchFocusAdapterOptions,
} from '@shared/business-workbench/workbench-focus-adapter';
import type { BusinessWorkspaceState } from '@shared/business-workbench/workbench-workspace-state';
import { ensureWorkbenchBootstrap } from '../../workbench/workbench-bootstrap';
import {
  resolvePaletteOperatingLayerCommands,
  type WorkbenchPaletteCommands,
} from '../../utils/globalSearchCommands';
import { useWorkbenchPermissionContext } from './useWorkbenchPermissionContext';

export interface UseResolvedWorkbenchCommandsOptions {
  query?: string;
  object?: UniversalBaseEntity;
  businessDomain?: string;
  status?: string;
  /** Pre-derived focus (e.g. from useOptionalDerivedWorkbenchFocus). */
  focus?: WorkbenchFocus;
  /** Derive focus from workspace SSOT when `focus` is omitted. */
  workspaceState?: BusinessWorkspaceState;
  focusAdapterOptions?: WorkbenchFocusAdapterOptions;
}

export function useResolvedWorkbenchCommands(
  options: UseResolvedWorkbenchCommandsOptions = {}
): WorkbenchPaletteCommands {
  const { role, plane, permissionKeys } = useWorkbenchPermissionContext();

  useEffect(() => {
    ensureWorkbenchBootstrap();
  }, []);

  const query = options.query ?? '';

  const resolvedFocus = useMemo(() => {
    if (options.focus !== undefined) {
      return options.focus;
    }
    if (options.workspaceState !== undefined) {
      return deriveWorkbenchFocusFromWorkspaceState(
        options.workspaceState,
        options.focusAdapterOptions ?? { actionProjection: 'commandPalette' }
      );
    }
    return undefined;
  }, [options.focus, options.workspaceState, options.focusAdapterOptions]);

  return useMemo(() => {
    const ctx: WorkbenchCommandContext = {
      role,
      plane,
      permissionKeys,
      ...(options.object !== undefined ? { object: options.object } : {}),
      ...(options.businessDomain !== undefined ? { businessDomain: options.businessDomain } : {}),
      ...(options.status !== undefined ? { status: options.status } : {}),
      ...(resolvedFocus !== undefined ? { focus: resolvedFocus } : {}),
    };
    return resolvePaletteOperatingLayerCommands(ctx, query);
  }, [
    role,
    plane,
    permissionKeys,
    query,
    options.object,
    options.businessDomain,
    options.status,
    resolvedFocus,
  ]);
}
